import { useVideoConfig, useCurrentFrame, interpolate, Sequence, Loop } from 'remotion';
import { Audio } from '@remotion/media';
import type { AppConfig } from '../lib/types';

export function useComposition(config: AppConfig, textStyle: React.CSSProperties) {
  const { height, fps, durationInFrames } = useVideoConfig();
  const frame = useCurrentFrame();
  const { renderMode, segments, text, audio } = config;
  const currentTime = frame / fps;

  const renderContent = () => {
    if (renderMode === 'sync') {
      let activeSegment = segments?.find(s => currentTime >= s.startTime && currentTime <= s.endTime);

      if (!activeSegment) {
        if (!segments || segments.length === 0) {
          activeSegment = {
            id: 'virtual-all',
            text: text,
            startTime: 0,
            endTime: durationInFrames / fps,
            transition: 'fade'
          };
        } else {
          const lastSegment = segments[segments.length - 1]!;
          if (currentTime > lastSegment.endTime) {
            const allLines = text.split('\n');
            const markedLinesCount = segments.reduce((acc, s) => acc + s.text.split('\n').length, 0);
            
            if (markedLinesCount < allLines.length) {
              const remainingText = allLines.slice(markedLinesCount).join('\n');
              activeSegment = {
                id: 'virtual-end',
                text: remainingText,
                startTime: lastSegment.endTime,
                endTime: durationInFrames / fps,
                transition: 'fade'
              };
            }
          }
        }
      }

      if (!activeSegment) return null;

      let opacity = 1;
      if (activeSegment.transition === 'fade') {
        const fadeDuration = 0.2;
        const timeInSegment = currentTime - activeSegment.startTime;
        const timeToEnd = activeSegment.endTime - currentTime;
        
        if (timeInSegment < fadeDuration) {
          opacity = timeInSegment / fadeDuration;
        } else if (timeToEnd < fadeDuration) {
          opacity = timeToEnd / fadeDuration;
        }
      }

      return (
        <div style={{ ...textStyle, opacity }}>
          {activeSegment.text}
        </div>
      );
    }

    const scrollY = interpolate(
      frame,
      [0, durationInFrames],
      [height / 2, -height],
      { extrapolateRight: 'clamp' }
    );

    return (
      <div style={{ 
        ...textStyle,
        position: 'absolute',
        top: scrollY,
        left: 0,
        padding: '0 10%',
      }}>
        {text}
      </div>
    );
  };

  const renderAudio = () => {
    const hasVO = !!audio.voiceoverUrl && !!audio.voiceoverDuration;
    const hasMusic = !!audio.musicUrl && !!audio.musicDuration;

    if (hasVO && hasMusic) {
      const voDurationFrames = Math.round(audio.voiceoverDuration! * fps);
      const musicDurationFrames = Math.round(audio.musicDuration! * fps);
      const startDelayFrames = 2 * fps;
      
      const voComponent = (
        <Sequence from={startDelayFrames} durationInFrames={voDurationFrames}>
          <Audio src={audio.voiceoverUrl!} volume={audio.voiceoverVolume} />
        </Sequence>
      );

      const fadeStartFrame = startDelayFrames + voDurationFrames;
      const fadeEndFrame = fadeStartFrame + (2 * fps);
      
      const musicVolume = interpolate(
        frame,
        [fadeStartFrame, fadeEndFrame],
        [audio.musicVolume, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );

      const musicComponent = (
        <Loop durationInFrames={musicDurationFrames}>
          <Audio src={audio.musicUrl!} volume={musicVolume} />
        </Loop>
      );

      return (
        <>
          {musicComponent}
          {voComponent}
        </>
      );
    }

    if (hasVO) {
      return <Audio src={audio.voiceoverUrl!} volume={audio.voiceoverVolume} />;
    }

    if (hasMusic) {
      return <Audio src={audio.musicUrl!} volume={audio.musicVolume} />;
    }

    return null;
  };

  return { renderContent, renderAudio };
}
